import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { brmAPI, socialAPI } from "@/services/api";
import { normalizeAvailableNiches, formatNicheLabel } from "@/lib/niches";
import FirebasePhoneAuth from "@/components/auth/FirebasePhoneAuth";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket, Users, Target, Link2, ChevronRight,
  ChevronLeft, CheckCircle2, Instagram, Facebook,
  Globe, Tag, Sparkles
} from "lucide-react";

const STEPS = [
  { id: 1, title: "Welcome to Proovam", icon: Rocket },
  { id: 2, title: "Connect Your Social", icon: Users },
  { id: 3, title: "Pick Your Niches", icon: Tag },
  { id: 4, title: "You're Ready!", icon: Sparkles },
];

const BRMOnboarding = ({ onComplete }) => {
  const navigate = useNavigate();
  const needsPhone = localStorage.getItem("proovam_requires_phone") === "true";
  const [step, setStep] = useState(needsPhone ? 0 : 1);
  const [availableNiches, setAvailableNiches] = useState([]);
  const [nichesLoading, setNichesLoading] = useState(true);
  const [selectedNiches, setSelectedNiches] = useState([]);
  const [savingNiches, setSavingNiches] = useState(false);
  const [skippedSocial, setSkippedSocial] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadNiches = async () => {
      setNichesLoading(true);
      try {
        const response = await socialAPI.getAvailableNiches();
        const normalized = normalizeAvailableNiches(response);
        if (!cancelled) setAvailableNiches(normalized.map((n) => n.slug));
      } catch {
        if (!cancelled) {
          setAvailableNiches([]);
          toast.error("Failed to load niches. Try again.");
        }
      } finally {
        if (!cancelled) setNichesLoading(false);
      }
    };

    loadNiches();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalSteps = needsPhone ? STEPS.length : STEPS.length - 1;
  const progress = step === 0 ? 0 : ((step - 1) / totalSteps) * 100;

  const toggleNiche = (nicheSlug) => {
    setSelectedNiches(prev =>
      prev.includes(nicheSlug) ? prev.filter(n => n !== nicheSlug) : [...prev, nicheSlug]
    );
  };

  const handleSaveNiches = async () => {
    if (selectedNiches.length === 0) {
      toast.error("Pick at least one niche so clients can find you.");
      return;
    }
    setSavingNiches(true);
    try {
      await socialAPI.setNiches({ niches: selectedNiches.map((niche) => niche.toLowerCase()) });
      setStep(4);
    } catch {
      toast.error("Failed to save niches. Try again.");
    } finally {
      setSavingNiches(false);
    }
  };

  const handleFinish = async () => {
    try {
      await brmAPI.completeOnboarding();
      localStorage.removeItem("proovam_requires_phone");
      if (onComplete) onComplete();
      navigate("/brm/campaigns");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not complete onboarding");
    }
  };

  const handlePhoneLinked = () => {
    localStorage.removeItem("proovam_requires_phone");
    toast.success("Phone verified!");
    setStep(1);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-gray-100 w-full">
        <motion.div
          className="h-1 bg-brand-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-2 pt-6 pb-2">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              s.id === step ? "bg-brand-500 w-6" : s.id < step ? "bg-brand-200" : "bg-gray-100"
            }`}
          />
        ))}
      </div>

      <div className="flex-1 px-5 py-4 flex flex-col">
        <AnimatePresence mode="wait">

          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="space-y-4 pt-4">
                <h1 className="text-2xl font-black tracking-tight">Verify your phone</h1>
                <p className="text-sm text-gray-500 leading-relaxed">
                  BRMs need a verified phone number for face-to-face enrollments and payouts.
                </p>
                <FirebasePhoneAuth linkOnly onLinked={handlePhoneLinked} />
              </div>
            </motion.div>
          )}

          {/* ── STEP 1: Welcome ── */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="space-y-6 pt-8">
                <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center">
                  <Rocket className="w-8 h-8 text-brand-500" />
                </div>
                <div>
                  <h1 className="text-3xl font-black tracking-tight mb-3">
                    You're a Brand Revenue Manager.
                  </h1>
                  <p className="text-gray-500 text-base leading-relaxed">
                    You promote products for companies and earn real commission
                    every time someone signs up, downloads, or subscribes - through
                    your unique link.
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { icon: Link2, title: "Your own deep link", desc: "Every campaign gives you a unique tracking URL." },
                    { icon: Target, title: "Earn on results", desc: "Commission hits your wallet the moment someone converts." },
                    { icon: Users, title: "Build your portfolio", desc: "More campaigns -> more income streams." },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="flex gap-3 p-3 rounded-xl bg-gray-50">
                      <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-brand-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{title}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button className="w-full h-12 rounded-xl mt-8" onClick={() => setStep(2)}>
                Let's set you up <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          )}

          {/* ── STEP 2: Connect Social ── */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="space-y-6 pt-4">
                <div>
                  <p className="text-xs font-bold text-brand-500 uppercase tracking-widest mb-1">Step 2 of 4</p>
                  <h2 className="text-2xl font-black tracking-tight">Connect your social accounts</h2>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                    Your follower count builds your rating. High-rated BRMs get access
                    to premium campaigns with bigger commissions.
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { platform: "instagram", label: "Instagram", Icon: Instagram, color: "text-pink-500", bg: "bg-pink-50" },
                    { platform: "tiktok", label: "TikTok", Icon: Globe, color: "text-black", bg: "bg-gray-50" },
                    { platform: "facebook", label: "Facebook", Icon: Facebook, color: "text-brand-500", bg: "bg-brand-50" },
                  ].map(({ platform, label, Icon, color, bg }) => (
                    <button
                      key={platform}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-brand-300 hover:bg-gray-50 transition-all text-left"
                      onClick={() => {
                        navigate("/brm/social");
                      }}
                    >
                      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">Connect {label}</p>
                        <p className="text-xs text-gray-500">Add a manual, unverified profile</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </button>
                  ))}
                </div>

                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs text-amber-700">
                      <strong>Tip:</strong> You can skip this and connect later from your Profile.
                      You can still join campaigns and earn - social profiles just boost your rating.
                    </p>
                </section>
              </div>

              <div className="space-y-3 mt-8">
                <Button className="w-full h-12 rounded-xl" onClick={() => setStep(3)}>
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-gray-500"
                  onClick={() => { setSkippedSocial(true); setStep(3); }}
                >
                  Skip for now
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Pick Niches ── */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="space-y-5 pt-4">
                <div>
                  <p className="text-xs font-bold text-brand-500 uppercase tracking-widest mb-1">Step 3 of 4</p>
                  <h2 className="text-2xl font-black tracking-tight">What's your world?</h2>
                  <p className="text-gray-500 text-sm mt-2">
                    Pick the niches you create content in. Clients use this to find
                    the right BRMs for their campaigns.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {nichesLoading ? (
                    <p className="text-sm text-gray-500">Loading niches...</p>
                  ) : availableNiches.length === 0 ? (
                    <p className="text-sm text-gray-500">No niches available right now. Try again later.</p>
                  ) : (
                    availableNiches.map((nicheSlug) => {
                      const selected = selectedNiches.includes(nicheSlug);
                      return (
                        <button
                          key={nicheSlug}
                          onClick={() => toggleNiche(nicheSlug)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            selected
                              ? "bg-brand-500 text-white border-brand-500"
                              : "bg-white border-gray-200 hover:border-brand-300"
                          }`}
                        >
                          {selected && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                          {formatNicheLabel(nicheSlug)}
                        </button>
                      );
                    })
                  )}
                </div>

                {selectedNiches.length > 0 && (
                  <p className="text-xs text-gray-500">
                    {selectedNiches.length} niche{selectedNiches.length > 1 ? "s" : ""} selected
                  </p>
                )}
              </div>

              <div className="space-y-3 mt-8">
                <Button
                  className="w-full h-12 rounded-xl"
                  onClick={handleSaveNiches}
                  disabled={savingNiches || nichesLoading || selectedNiches.length === 0}
                >
                  {savingNiches ? "Saving..." : "Save & Continue"}
                  {!savingNiches && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-gray-500 text-sm"
                  onClick={() => setStep(2)}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 4: Ready ── */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 flex flex-col justify-between"
            >
              <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6 pt-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-20 h-20 rounded-full bg-brand-50 flex items-center justify-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-brand-500" />
                </motion.div>

                <div>
                  <h2 className="text-3xl font-black tracking-tight mb-3">You're all set!</h2>
                  <p className="text-gray-500 leading-relaxed">
                    Head to the Campaign Marketplace, join a campaign that matches
                    your audience, and share your unique deep-link to start earning.
                  </p>
                </div>

                <section className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left">
                  <div className="space-y-2">
                    <p className="font-semibold text-sm">How you earn:</p>
                    {[
                      "Join a campaign -> get your unique link",
                      "Share the link on social, WhatsApp, anywhere",
                      "Someone clicks and signs up within 30 days",
                      "Commission lands in your wallet 💰",
                    ].map((step, i) => (
                      <div key={i} className="flex gap-2 text-xs text-gray-500">
                        <span className="text-brand-500 font-bold">{i + 1}.</span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {skippedSocial && (
                  <section className="w-full rounded-2xl border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs text-amber-700">
                        Don't forget to connect your social profiles later - it boosts your
                        rating and unlocks premium campaigns.
                      </p>
                  </section>
                )}
              </div>

              <Button className="w-full h-12 rounded-xl mt-6" onClick={handleFinish}>
                Browse Campaigns <Rocket className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

export default BRMOnboarding;
